import Logo from '@assets/icon.svg';
import { selectCurrentUser, selectUserOutlet } from '@store/auth/authSelectors';
import {
  fetchCurrentUser,
  register,
  setupAccount,
  verifyEmail,
} from '@store/auth/authThunks';
import {
  completeOnboarding,
  completePaymentSetup,
  nextStep,
  previousStep,
  setCurrentStep,
  updateFormData,
} from '@store/onboardingSlice';
import {
  selectAuthLoading,
  selectOnboardingError,
  selectOnboardingFormData,
  selectOnboardingLoading,
  selectOnboardingStep,
  selectSubscriptionPackages,
} from '@store/selectors';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import businessHero from '../../assets/business-hero.png';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { resendVerificationCode } from '../../services/authService';
import {
  fetchCompanyBilling,
  isBillingSetupComplete,
  subscribeToCompanyBilling,
  updatePendingBillingSelection,
} from '../../services/billingService';
import { BillingCycle, BillingRecord } from '../../types/billing';
import { OnboardingStep } from '../../types/onboarding';
import { apiError } from '../../util/notify';
import { isElectron } from '../../util';
import { BusinessInfoStep } from './BusinessInfoStep';
import { CompletionStep } from './CompletionStep';
import { EmailVerificationStep } from './EmailVerificationStep';
import { PackageSelectionStep } from './PackageSelectionStep';
import { PaymentSetupStep } from './PaymentSetupStep';
import { PersonalInfoStep } from './PersonalInfoStep';

export default function OnboardingFlow() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentStep = useAppSelector(selectOnboardingStep);
  const formData = useAppSelector(selectOnboardingFormData);
  const onboardingError = useAppSelector(selectOnboardingError);
  const onboardingLoading = useAppSelector(selectOnboardingLoading);
  const authLoading = useAppSelector(selectAuthLoading);
  const currentUser = useAppSelector(selectCurrentUser);
  const packages = useAppSelector(selectSubscriptionPackages);
  const userOutlet = useAppSelector(selectUserOutlet);

  // Combine errors
  const error = onboardingError;

  const [signupData, setSignupData] = useState<{
    companyId?: number;
    outletId?: string;
    trialEndsAt?: string;
  }>({});
  const [checkoutSession, setCheckoutSession] = useState<{
    sessionId: string;
    sessionUrl: string;
  } | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isWaitingForBillingSync, setIsWaitingForBillingSync] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const autoCompletionInFlightRef = useRef(false);
  const resumeStateRef = useRef<string | null>(null);
  const handleBillingActivationRef = useRef<
    (billingRecord: BillingRecord) => Promise<void>
  >(async () => undefined);

  const selectedPackageInfo = packages.find(
    (pkg) => pkg.id === formData.package,
  );
  const resolvedCompanyId = signupData.companyId ?? currentUser?.company?.id;
  const resolvedOutletId =
    signupData.outletId ?? currentUser?.outlet_id ?? userOutlet?.id;
  const resolvedTrialEndsAt =
    signupData.trialEndsAt ?? currentUser?.subscription?.trial_ends_at;
  const onboardingCompanyId = resolvedCompanyId;
  const hasCheckoutSession = Boolean(checkoutSession?.sessionId);
  const hasExistingOutlet = Boolean(resolvedOutletId);
  const hasExistingBillingRecord = Boolean(currentUser?.subscription?.id);
  const hasCompletedBilling = Boolean(
    currentUser?.subscription?.stripe_customer_id &&
    currentUser?.subscription?.stripe_subscription_id,
  );
  const resumeStateKey = currentUser?.id
    ? [
        currentUser.id,
        hasExistingOutlet ? 'outlet-ready' : 'outlet-missing',
        hasExistingBillingRecord ? 'billing-record' : 'billing-record-missing',
        hasCompletedBilling ? 'billing-complete' : 'billing-pending',
      ].join(':')
    : null;

  useEffect(() => {
    if (!currentUser?.id) {
      resumeStateRef.current = null;
      return;
    }

    setSignupData((current) => ({
      companyId: current.companyId ?? currentUser.company?.id,
      outletId: current.outletId ?? currentUser.outlet_id ?? userOutlet?.id,
      trialEndsAt:
        current.trialEndsAt ?? currentUser.subscription?.trial_ends_at,
    }));

    const resumeFormData: Partial<typeof formData> = {};

    if (!formData.businessName && currentUser.company?.business_name) {
      resumeFormData.businessName = currentUser.company.business_name;
    }

    if (!formData.businessType && currentUser.company?.business_type) {
      resumeFormData.businessType = currentUser.company.business_type;
    }

    if (!formData.countryId && currentUser.company?.country_id) {
      resumeFormData.countryId = currentUser.company.country_id;
    }

    if (!formData.package && currentUser.subscription?.package) {
      resumeFormData.package = currentUser.subscription.package;
    }

    if (!formData.billingCycle) {
      resumeFormData.billingCycle =
        currentUser.subscription?.billing_cycle || 'monthly';
    }

    if (Object.keys(resumeFormData).length > 0) {
      dispatch(updateFormData(resumeFormData));
    }
  }, [
    currentUser,
    dispatch,
    formData.billingCycle,
    formData.businessName,
    formData.businessType,
    formData.countryId,
    formData.package,
    userOutlet?.id,
  ]);

  useEffect(() => {
    if (!currentUser?.id || !resumeStateKey) {
      return;
    }

    if (resumeStateRef.current === resumeStateKey) {
      return;
    }

    resumeStateRef.current = resumeStateKey;

    if (hasCompletedBilling) {
      dispatch(completeOnboarding());
      navigate('/dashboard/getting-started', { replace: true });
      return;
    }

    if (!hasExistingOutlet) {
      if (currentStep !== OnboardingStep.BUSINESS_INFO) {
        dispatch(setCurrentStep(OnboardingStep.BUSINESS_INFO));
      }
      return;
    }

    if (hasExistingBillingRecord) {
      if (currentStep !== OnboardingStep.PAYMENT_SETUP) {
        dispatch(setCurrentStep(OnboardingStep.PAYMENT_SETUP));
      }
      return;
    }

    if (currentStep !== OnboardingStep.PACKAGE_SELECTION) {
      dispatch(setCurrentStep(OnboardingStep.PACKAGE_SELECTION));
    }
  }, [
    currentStep,
    currentUser?.id,
    dispatch,
    hasCompletedBilling,
    hasExistingBillingRecord,
    hasExistingOutlet,
    navigate,
    resumeStateKey,
  ]);

  handleBillingActivationRef.current = async (billingRecord: BillingRecord) => {
    if (autoCompletionInFlightRef.current) {
      return;
    }

    autoCompletionInFlightRef.current = true;
    setIsWaitingForBillingSync(false);
    setPaymentError(null);
    setSignupData((current) => ({
      ...current,
      trialEndsAt: billingRecord.trial_ends_at || current.trialEndsAt,
    }));

    dispatch(completeOnboarding());
    navigate('/dashboard/getting-started', { replace: true });
    await dispatch(fetchCurrentUser());
  };

  const handlePersonalInfoNext = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => {
    dispatch(updateFormData(data));
    const result = await dispatch(register(data));
    if (register.fulfilled.match(result)) {
      dispatch(nextStep());
    }
  };

  const handleEmailVerificationNext = async (code: string) => {
    if (formData.email) {
      const result = await dispatch(
        verifyEmail({ email: formData.email, token: code }),
      );
      if (verifyEmail.fulfilled.match(result)) {
        dispatch(nextStep());
      }
    }
  };

  const handleResendVerificationCode = async () => {
    if (!formData.email) {
      return;
    }

    setIsResending(true);
    try {
      const result = await resendVerificationCode(formData.email);

      if (!result.success) {
        apiError('Failed to resend verification code. Please try again');
      }
    } catch (err) {
      const authError =
        err instanceof Error
          ? err.message
          : 'Failed to resend verification code. Please try again';
      apiError(authError);
    } finally {
      setIsResending(false);
    }
  };

  const handleBusinessInfoNext = (data: {
    businessName: string;
    businessType: any;
    countryId: number;
  }) => {
    dispatch(updateFormData(data));
    dispatch(nextStep());
  };

  const handlePackageSelectionNext = async (
    packageId: any,
    billingCycle: BillingCycle,
  ) => {
    autoCompletionInFlightRef.current = false;
    dispatch(updateFormData({ billingCycle, package: packageId }));
    setCheckoutSession(null);
    setPaymentError(null);
    setIsWaitingForBillingSync(false);

    if (resolvedCompanyId) {
      const result = await updatePendingBillingSelection(
        resolvedCompanyId,
        packageId,
        billingCycle,
      );

      if (result.success) {
        setSignupData((current) => ({
          ...current,
          trialEndsAt: result.data?.trialEndsAt || current.trialEndsAt,
        }));
        dispatch(nextStep());
      } else {
        apiError(result.error || 'Failed to update your plan selection.');
      }

      return;
    }

    // Create Company/Outlet/Billing NOW - after package selection
    const completeFormData = {
      ...formData,
      billingCycle,
      package: packageId,
    };

    if (
      completeFormData.businessName &&
      completeFormData.businessType &&
      completeFormData.countryId
    ) {
      const result = await dispatch(setupAccount(completeFormData as any));

      if (setupAccount.fulfilled.match(result)) {
        setSignupData({
          companyId: (result.payload as any)?.companyId,
          outletId: (result.payload as any)?.outletId,
          trialEndsAt: (result.payload as any)?.trialEndsAt,
        });
        dispatch(nextStep());
      }
    }
  };

  const openExternalUrl = async (url: string) => {
    if (isElectron()) {
      await window.electron.ipcRenderer.invoke('open-external', url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenCheckout = async () => {
    if (checkoutSession?.sessionUrl) {
      setPaymentError(null);
      setIsWaitingForBillingSync(true);
      await openExternalUrl(checkoutSession.sessionUrl);
      return;
    }

    if (resolvedCompanyId && formData.package) {
      setPaymentError(null);
      setIsWaitingForBillingSync(false);
      autoCompletionInFlightRef.current = false;
      const result = await dispatch(
        completePaymentSetup({
          billingCycle: formData.billingCycle || 'monthly',
          packageId: formData.package,
          companyId: resolvedCompanyId,
        }),
      );

      if (completePaymentSetup.fulfilled.match(result)) {
        setCheckoutSession(result.payload);
        setIsWaitingForBillingSync(true);
        await openExternalUrl(result.payload.sessionUrl);
        return;
      }

      setPaymentError(
        (result.payload as string) || 'Failed to open secure checkout.',
      );
      setIsWaitingForBillingSync(false);
      return;
    }

    setPaymentError(
      'We could not find your selected package. Please choose a plan again.',
    );
    setIsWaitingForBillingSync(false);
  };

  const handlePaymentStepBack = () => {
    autoCompletionInFlightRef.current = false;
    setCheckoutSession(null);
    setPaymentError(null);
    setIsWaitingForBillingSync(false);
    dispatch(previousStep());
  };

  useEffect(() => {
    if (currentStep !== OnboardingStep.PAYMENT_SETUP || !onboardingCompanyId) {
      return undefined;
    }

    let disposed = false;
    let pollingInterval: number | null = null;

    const syncBillingStatus = async () => {
      const response = await fetchCompanyBilling(onboardingCompanyId);

      if (
        disposed ||
        !response.success ||
        !isBillingSetupComplete(response.data)
      ) {
        return;
      }

      await handleBillingActivationRef.current(response.data);
    };
    const triggerBillingSync = () => {
      syncBillingStatus().catch(() => undefined);
    };

    const unsubscribe = subscribeToCompanyBilling(
      onboardingCompanyId,
      triggerBillingSync,
    );

    triggerBillingSync();

    if (hasCheckoutSession) {
      pollingInterval = window.setInterval(() => {
        triggerBillingSync();
      }, 2500);
    }

    return () => {
      disposed = true;
      unsubscribe();
      if (pollingInterval) {
        window.clearInterval(pollingInterval);
      }
    };
  }, [currentStep, hasCheckoutSession, onboardingCompanyId]);

  const handleFinish = () => {
    dispatch(completeOnboarding());
    navigate('/dashboard/getting-started');
  };

  const renderStep = () => {
    switch (currentStep) {
      case OnboardingStep.PERSONAL_INFO:
        return (
          <PersonalInfoStep
            initialData={formData}
            onNext={handlePersonalInfoNext}
            isLoading={authLoading}
          />
        );
      case OnboardingStep.EMAIL_VERIFICATION:
        return (
          <EmailVerificationStep
            email={formData.email || ''}
            onVerify={handleEmailVerificationNext}
            onResend={handleResendVerificationCode}
            isLoading={authLoading}
            isResending={isResending}
            error={error}
          />
        );
      case OnboardingStep.BUSINESS_INFO:
        return (
          <BusinessInfoStep
            initialData={formData}
            onNext={handleBusinessInfoNext}
            onBack={() => dispatch(previousStep())}
            isLoading={false}
          />
        );
      case OnboardingStep.PACKAGE_SELECTION:
        return (
          <PackageSelectionStep
            initialBillingCycle={formData.billingCycle}
            initialPackage={formData.package}
            onNext={handlePackageSelectionNext}
            onBack={() => dispatch(previousStep())}
            isSubmitting={authLoading}
          />
        );
      case OnboardingStep.PAYMENT_SETUP:
        return resolvedCompanyId ? (
          <PaymentSetupStep
            billingCycle={formData.billingCycle || 'monthly'}
            checkoutStarted={Boolean(checkoutSession)}
            error={paymentError || error}
            isLaunchingCheckout={onboardingLoading}
            isWaitingForBillingConfirmation={isWaitingForBillingSync}
            onOpenCheckout={handleOpenCheckout}
            selectedPackage={formData.package}
            trialEndsAt={resolvedTrialEndsAt}
            onBack={handlePaymentStepBack}
          />
        ) : null;
      case OnboardingStep.COMPLETE:
        return (
          <CompletionStep
            businessName={formData.businessName || 'Your Business'}
            billingCycle={formData.billingCycle || 'monthly'}
            onFinish={handleFinish}
            packageName={selectedPackageInfo?.name}
            trialEndsAt={resolvedTrialEndsAt}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full bg-white rounded-2xl shadow-xl overflow-hidden flex min-h-[600px] h-[75vh] max-h-[800px]">
        <div className="w-full lg:w-1/2 flex flex-col h-full overflow-hidden">
          <div className="flex-shrink-0 px-10 pt-8 pb-4 bg-white border-b border-gray-100">
            <div className="max-w-md mx-auto w-full">
              <div className="flex items-center gap-2">
                <img src={Logo} alt="SmartPOS Logo" className="w-8 h-8" />
                <span className="text-xl font-bold text-gray-900 font-sans">
                  SmartPOS Cloud
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar px-10 py-6">
            <div className="max-w-md mx-auto w-full">
              {!currentUser && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="flex items-center text-sm text-gray-500 hover:text-pos-primary transition-colors"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Back to Login
                  </button>
                </div>
              )}

              {currentStep < OnboardingStep.COMPLETE && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase text-gray-500 tracking-wider">
                      Step {currentStep + 1} of 5
                    </span>
                    <span className="text-xs font-medium text-pos-primary">
                      {currentStep === 0 && 'Personal Info'}
                      {currentStep === 1 && 'Verify Email'}
                      {currentStep === 2 && 'Business Details'}
                      {currentStep === 3 && 'Select Package'}
                      {currentStep === 4 && 'Payment'}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-pos-primary transition-all duration-500 ease-out"
                      style={{ width: `${((currentStep + 1) / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  <p className="font-medium">
                    Please correct the following error:
                  </p>
                  <p>{error}</p>
                </div>
              )}

              <div className="onboarding-step-container">{renderStep()}</div>
            </div>
          </div>
        </div>

        <div className="hidden lg:block w-1/2 relative bg-gray-900">
          <img
            src={businessHero}
            alt="SmartPOS Onboarding"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-pos-dark/80 via-pos-dark/20 to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-10 text-white">
            <h2 className="text-3xl font-bold mb-3 font-sans text-white">
              Start your journey
            </h2>
            <p className="text-gray-200 text-lg font-light leading-relaxed">
              Join thousands of businesses managing their sales, inventory, and
              customers with SmartPOS Cloud.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
